/**
 * Characters that never belong in a name, a job title, a unit, a note or a search: control
 * characters (a NUL byte, a line break, a tab), invisible format characters (bidirectional
 * overrides that reorder what is shown, zero-width spaces, a byte-order mark) and lone halves of
 * a surrogate pair, which are not text at all (no browser sends one, but a request made by hand
 * can: PostgreSQL would store it as U+FFFD, or refuse the JSON it comes in). The zero-width
 * joiner and non-joiner stay allowed, because some scripts need them to write words correctly.
 */
const UNSAFE = /(?![\u200C\u200D])[\p{Cc}\p{Cf}\p{Cs}]/u
const UNSAFE_ALL = new RegExp(UNSAFE.source, 'gu')
/** The control characters that separate words: a tab, a line break, a page break. */
const BREAKS = /[\t\n\v\f\r\u0085]+/gu

/** Whether free text carries a character that must never be stored or searched for. */
export function hasUnsafeCharacters(value: string): boolean {
  return UNSAFE.test(value)
}

/**
 * The line and paragraph separators (U+2028, U+2029): not control characters, so the rule above
 * lets them through, but a browser or a mail client breaks the line at either.
 */
const LINE_SEPARATORS = /[\u2028\u2029]/u

/**
 * Whether the text is one line with nothing invisible in it: no character `hasUnsafeCharacters`
 * finds (a line break is one of them) and no line or paragraph separator either. HR's notes are
 * (ADR 0005 §3), and so are the titles that end up in an email or on a diploma (ADR 0009 §4).
 */
export function isSafeLine(value: string): boolean {
  return !hasUnsafeCharacters(value) && !LINE_SEPARATORS.test(value)
}

/** The control characters a text of several lines may have: line breaks and tabs. */
const LINE_BREAKS = /[\t\n\r]/gu

/**
 * Whether a text of several lines (a description, a question, an explanation) has nothing
 * invisible in it: what `hasUnsafeCharacters` finds, except line breaks and tabs.
 */
export function isSafeMultilineText(value: string): boolean {
  return !hasUnsafeCharacters(value.replace(LINE_BREAKS, ''))
}

/**
 * The text without those characters, for what a person types or pastes into a search: the
 * server refuses them, and they are invisible, so the person could not see what to correct.
 * A tab or a line break between two words (two cells of a spreadsheet, two lines) becomes a
 * space, as it is shown, instead of joining the words.
 */
export function withoutUnsafeCharacters(value: string): string {
  return value.replace(BREAKS, ' ').replace(UNSAFE_ALL, '')
}

/**
 * How many characters the text has as PostgreSQL counts them (`length()`): code points, so an
 * emoji is one character, where JavaScript's `length` counts the two UTF-16 units it takes. A
 * limit the database checks too (a note's 500 characters) is counted this way on both sides.
 */
export function characterCount(value: string): number {
  return [...value].length
}

/**
 * The text cut to fit in `max` as JavaScript counts its length, which is what a limit of a course's
 * validators counts, never in the middle of a character as it is seen: not a surrogate pair, a letter
 * and its combining accent, nor an emoji joined to the next. Without the space a cut may leave at
 * its end.
 */
export function cutToLength(value: string, max: number): string {
  if (value.length <= max) return value
  let kept = ''
  for (const { segment } of new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(
    value,
  )) {
    if (kept.length + segment.length > max) break
    kept += segment
  }
  return kept.trimEnd()
}
